import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Copy, Check, Send, ArrowLeft, AlertCircle, RefreshCw } from 'lucide-react';

const SimulatedWalletFlow = ({ phase = 'buy', onComplete }) => {
  const [step, setStep] = useState('form'); // form -> submitting -> pending -> approved/done
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bs');
  const [reference, setReference] = useState('');
  const [txHash, setTxHash] = useState('');
  const [cedula, setCedula] = useState('');
  const [bank, setBank] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [copiedBank, setCopiedBank] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const timeoutRef = useRef(null);

  const bankData = `0102 Venezuela\n20827955\n0412-225.00.16\nPago`;
  const usdtWallet = 'TLNsU1jj17zvRHbrJW27Xv9DGK9KiQBD3y';
  const firesPerUsdt = 100;

  const isBuy = phase === 'buy';
  const parsedAmount = parseFloat(amount) || 0;
  const commission = isBuy ? 0 : parsedAmount * 0.05;
  const total = isBuy ? parsedAmount : parsedAmount + commission;
  const estimatedUsdt = !isBuy && parsedAmount > 0 ? parsedAmount / firesPerUsdt : 0;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleSubmit = useCallback(() => {
    setError('');
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Ingresa un monto valido');
      return;
    }
    if (!isBuy && parsedAmount < 100) {
      setError('El retiro minimo es 100 fuegos');
      return;
    }
    if (isBuy) {
      if (method === 'bs' && !reference.trim()) {
        setError('Ingresa la referencia bancaria');
        return;
      }
      if (method === 'usdt' && !txHash.trim()) {
        setError('Ingresa el hash de la transaccion');
        return;
      }
    } else {
      if (!cedula.trim() || !bank.trim() || !phone.trim()) {
        setError('Completa todos los datos bancarios');
        return;
      }
    }

    setStep('submitting');
    timeoutRef.current = setTimeout(() => {
      setStep('pending');
      timeoutRef.current = setTimeout(() => {
        setStep(isBuy ? 'approved' : 'processed');
      }, 3000);
    }, 1500);
  }, [parsedAmount, isBuy, method, reference, txHash, cedula, bank, phone]);

  const handleContinue = useCallback(() => {
    if (typeof onComplete === 'function') onComplete();
  }, [onComplete]);

  return (
    <div className="tutorial-simulated-wallet">
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.3 }}
        className="tutorial-wallet-card"
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleContinue}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="w-10 h-10 rounded-full bg-fire-orange/20 flex items-center justify-center">
              {isBuy ? <ShoppingCart size={20} className="text-fire-orange" /> : <RefreshCw size={20} className="text-fire-orange" />}
            </div>
            <h2 className="text-xl font-bold">{isBuy ? 'Comprar Fuegos' : 'Solicitar Retiro'}</h2>
          </div>
          <button
            onClick={handleContinue}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <AnimatePresence mode="wait">
          {step === 'form' && (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <label className="block text-xs text-white/70 mb-1.5">
                Monto de fuegos
              </label>
              <input
                type="number"
                min={isBuy ? 1 : 100}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={isBuy ? 'Ej: 1000' : 'Minimo 100'}
                className="tutorial-wallet-input"
              />

              {!isBuy && parsedAmount > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="tutorial-wallet-fee-info"
                >
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/70">Monto neto</span>
                    <span className="text-white font-semibold">{parsedAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white/70">Comision (5%)</span>
                    <span className="text-orange-300 font-semibold">+{commission.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2 pt-2 border-t border-white/10">
                    <span className="text-white/80 font-semibold">Total a deducir</span>
                    <span className="text-orange-400 font-bold">{total.toFixed(2)}</span>
                  </div>
                </motion.div>
              )}

              {isBuy && (
                <>
                  <div className="flex bg-white/5 rounded-lg p-1 text-sm border border-white/10">
                    <button
                      type="button"
                      onClick={() => setMethod('bs')}
                      className={`flex-1 py-2 rounded-md transition-colors ${
                        method === 'bs'
                          ? 'bg-fire-orange/80 text-dark font-semibold'
                          : 'bg-transparent text-text/60 hover:bg-white/5'
                      }`}
                    >
                      Pago en Bs (Transferencia)
                    </button>
                    <button
                      type="button"
                      onClick={() => setMethod('usdt')}
                      className={`flex-1 py-2 rounded-md transition-colors ml-1 ${
                        method === 'usdt'
                          ? 'bg-emerald-500/80 text-dark font-semibold'
                          : 'bg-transparent text-text/60 hover:bg-white/5'
                      }`}
                    >
                      Pago en USD (TRON)
                    </button>
                  </div>

                  {method === 'bs' ? (
                    <>
                      <div className="bg-accent/10 border border-accent/30 rounded-lg p-3 mt-3">
                        <p className="text-xs text-accent">
                          💡 <strong>Pasos:</strong>
                          <br />1. Realiza el pago a los datos bancarios
                          <br />2. Ingresa la cantidad de fuegos y tu referencia
                          <br />3. Espera la aprobación del administrador
                        </p>
                      </div>

                      <div className="glass-panel p-4 space-y-3 mt-3">
                        <label className="text-sm font-medium text-text/80">
                          Datos Bancarios para Pago
                        </label>
                        <div className="bg-background-dark/50 rounded-lg p-3">
                          <pre className="text-sm text-accent whitespace-pre-wrap font-mono">
                            {bankData}
                          </pre>
                        </div>
                        <button
                          onClick={() => { setCopiedBank(true); setTimeout(() => setCopiedBank(false), 2000); }}
                          className="w-full py-2 px-4 bg-white/5 hover:bg-white/10 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          {copiedBank ? (
                            <><Check size={16} /> ¡Copiado!</>
                          ) : (
                            <><Copy size={16} /> Copiar Datos Bancarios</>
                          )}
                        </button>
                      </div>

                      <div className="mt-3">
                        <label className="block text-sm font-medium text-text/80 mb-2">
                          Cantidad de Fuegos a Solicitar
                        </label>
                        <input
                          type="number"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="Ej: 100"
                          min="1"
                          step="0.01"
                          className="input-glass w-full"
                        />
                        <p className="text-xs text-text/40 mt-1">
                          El valor en Bs se calcula con la tasa MundoXYZ vigente al registrar tu solicitud.
                        </p>
                      </div>

                      <div className="mt-3">
                        <label className="block text-sm font-medium text-text/80 mb-2">
                          Referencia Bancaria (solo números)
                        </label>
                        <input
                          type="text"
                          value={reference}
                          onChange={(e) => setReference(e.target.value.replace(/\D/g, ''))}
                          placeholder="123456789"
                          inputMode="numeric"
                          className="input-glass w-full"
                        />
                        <p className="text-xs text-text/40 mt-1">
                          Ingresa solo los números de tu comprobante de pago
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3 mt-3">
                        <p className="text-xs text-emerald-300">
                          💡 <strong>Pasos:</strong>
                          <br />1. Envía USD (TRON) a la billetera oficial
                          <br />2. Ingresa el monto en USD y el hash de la transacción
                          <br />3. Espera la aprobación del administrador
                        </p>
                      </div>

                      <div className="glass-panel p-4 space-y-3 mt-3">
                        <label className="text-sm font-medium text-text/80">
                          Billetera oficial USD (TRON)
                        </label>
                        <div className="bg-background-dark/50 rounded-lg p-3 overflow-x-auto">
                          <div className="text-xs sm:text-sm text-emerald-300 font-mono break-all">
                            {usdtWallet}
                          </div>
                        </div>
                        <button
                          onClick={() => { setCopiedWallet(true); setTimeout(() => setCopiedWallet(false), 2000); }}
                          className="w-full py-2 px-4 bg-white/5 hover:bg-white/10 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          {copiedWallet ? (
                            <><Check size={16} /> ¡Copiado!</>
                          ) : (
                            <><Copy size={16} /> Copiar Billetera USD</>
                          )}
                        </button>
                      </div>

                      <div className="mt-3">
                        <label className="block text-sm font-medium text-text/80 mb-2">
                          Monto a enviar en USD
                        </label>
                        <input
                          type="number"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="Ej: 1.00"
                          min="0.01"
                          step="0.01"
                          className="input-glass w-full"
                        />
                        {parsedAmount > 0 && (
                          <p className="text-xs text-text/40 mt-1">
                            Recibirás aproximadamente {(parsedAmount * firesPerUsdt).toFixed(2)} 🔥
                          </p>
                        )}
                      </div>

                      <div className="mt-3">
                        <label className="block text-sm font-medium text-text/80 mb-2">
                          Hash de Transacción USD
                        </label>
                        <input
                          type="text"
                          value={txHash}
                          onChange={(e) => setTxHash(e.target.value)}
                          placeholder="Ej: 0x..."
                          className="input-glass w-full"
                        />
                        <p className="text-xs text-text/40 mt-1">
                          Pega aquí el hash de la transacción en TRON
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}

              {!isBuy && (
                <>
                  <div>
                    <label className="block text-sm text-text/60 mb-1">Cantidad de Fuegos *</label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      min={method === 'usdt' ? 100 : 100}
                      step="1"
                      className="input-glass w-full"
                      placeholder="Ej: 1000"
                    />
                    <p className="text-[11px] text-text/40 mt-1">
                      {method === 'usdt' ? 'Mínimo: 100 fuegos (retiro en USD)' : 'Mínimo: 100 fuegos'}
                    </p>
                  </div>

                  <div className="bg-white/5 rounded-2xl p-4 border border-white/10 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-text/60">Cantidad a retirar:</span>
                      <span className="text-text font-bold">{parsedAmount.toFixed(2)} 🔥</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text/60">Comisión plataforma (5%):</span>
                      <span className="text-warning font-bold">{commission.toFixed(2)} 🔥</span>
                    </div>
                    <div className="border-t border-white/10 pt-3 flex justify-between">
                      <span className="text-text font-black">Total a deducir:</span>
                      <span className="text-fire-orange font-black text-lg">{total.toFixed(2)} 🔥</span>
                    </div>
                  </div>

                  <div className="flex bg-white/5 rounded-xl p-1 text-sm border border-white/10">
                    <button
                      type="button"
                      onClick={() => setMethod('bs')}
                      className={`flex-1 py-2.5 rounded-lg transition-all ${
                        method === 'bs'
                          ? 'bg-gradient-to-r from-fire-orange to-fire-yellow text-dark font-black'
                          : 'text-text/60 hover:text-text hover:bg-white/5'
                      }`}
                    >
                      Bolívares (Bs)
                    </button>
                    <button
                      type="button"
                      onClick={() => setMethod('usdt')}
                      className={`flex-1 py-2.5 rounded-lg transition-all ${
                        method === 'usdt'
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-400 text-dark font-black'
                          : 'text-text/60 hover:text-text hover:bg-white/5'
                      }`}
                    >
                      USDT (TRON)
                    </button>
                  </div>

                  {method === 'bs' ? (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Cédula</label>
                          <input
                            type="text"
                            value={cedula}
                            onChange={(e) => setCedula(e.target.value)}
                            placeholder="V12345678"
                            className="input-glass w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Teléfono</label>
                          <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+58412..."
                            className="input-glass w-full"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Banco</label>
                        <input
                          type="text"
                          value={bank}
                          onChange={(e) => setBank(e.target.value)}
                          placeholder="Banco de Venezuela"
                          className="input-glass w-full"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Cédula</label>
                          <input
                            type="text"
                            value={cedula}
                            onChange={(e) => setCedula(e.target.value)}
                            placeholder="V12345678"
                            className="input-glass w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Teléfono</label>
                          <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+58412..."
                            className="input-glass w-full"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Wallet Address (TRC-20)</label>
                        <input
                          type="text"
                          value={txHash}
                          onChange={(e) => setTxHash(e.target.value)}
                          placeholder="TL..."
                          className="input-glass w-full"
                        />
                      </div>
                    </>
                  )}

                  <div className="bg-fire-orange/10 border border-fire-orange/20 rounded-xl p-3 flex gap-3">
                    <AlertCircle size={20} className="text-fire-orange flex-shrink-0" />
                    <div className="text-[11px] text-text/70 leading-normal">
                      {method === 'usdt' ? (
                        <>
                          Recibirás aproximadamente <span className="font-bold text-text">{estimatedUsdt.toFixed(2)} USD</span>. 
                          La tasa es 1 USD ≈ {firesPerUsdt} 🔥. 
                        </>
                      ) : (
                        <>
                          Recibirás <span className="font-bold text-text">{parsedAmount > 0 ? (parsedAmount / firesPerUsdt * 40).toFixed(2) : '---'} Bs</span>. 
                          Tasa referencial.
                        </>
                      )}
                      {" "}El proceso puede tardar hasta 48 horas hábiles.
                    </div>
                  </div>
                </>
              )}

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-red-400 mb-3"
                >
                  {error}
                </motion.p>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleContinue}
                  className="flex-1 py-3 px-4 rounded-xl bg-white/5 text-text hover:bg-white/10 transition-colors font-bold"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-fire-orange to-fire-yellow text-dark font-black shadow-fire hover:scale-[1.02] transition-transform disabled:opacity-50"
                >
                  {isBuy ? (
                    <span className="flex items-center justify-center gap-2">
                      <Send size={18} />
                      Enviar Solicitud
                    </span>
                  ) : (
                    'Confirmar Retiro'
                  )}
                </button>
              </div>

              {isBuy && (
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-3">
                  <p className="text-xs text-warning">
                    ⚠️ Tu solicitud será revisada por un administrador. Asegúrate de que la referencia sea correcta.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {step === 'submitting' && (
            <motion.div
              key="submitting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-8"
            >
              <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-400 rounded-full animate-spin mb-4" />
              <p className="text-sm text-white/80">Enviando solicitud...</p>
            </motion.div>
          )}

          {step === 'pending' && (
            <motion.div
              key="pending"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-4"
            >
              <div className="w-14 h-14 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">⏳</span>
              </div>
              <h4 className="font-bold text-white mb-1">
                Solicitud enviada
              </h4>
              <p className="text-xs text-white/60 mb-4">
                #{isBuy ? 'REQ-7823' : 'RET-4521'} · Estado:
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-500/20 text-yellow-300 ml-1">Pendiente</span>
              </p>
              <div className="bg-white/5 rounded-lg p-3 text-left text-xs space-y-1.5 mb-4">
                <div className="flex justify-between">
                  <span className="text-white/60">Monto</span>
                  <span className="text-white font-semibold">{parsedAmount.toFixed(2)} 🔥</span>
                </div>
                {!isBuy && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-white/60">Comision (5%)</span>
                      <span className="text-orange-300">+{commission.toFixed(2)} 🔥</span>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-white/10">
                      <span className="text-white/80 font-semibold">Total deducido</span>
                      <span className="text-orange-400 font-bold">{total.toFixed(2)} 🔥</span>
                    </div>
                  </>
                )}
              </div>
              <p className="text-[11px] text-white/50">
                Un administrador revisara tu solicitud pronto.
              </p>
              <button
                type="button"
                onClick={handleContinue}
                className="mt-4 py-2 px-4 rounded-xl bg-white/5 text-text hover:bg-white/10 transition-colors font-bold text-sm"
              >
                Continuar
              </button>
            </motion.div>
          )}

          {step === 'approved' && (
            <motion.div
              key="approved"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-4"
            >
              <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <Check size={32} className="text-green-400" />
              </div>
              <h4 className="font-bold text-white mb-1">
                Solicitud aprobada
              </h4>
              <p className="text-xs text-white/60 mb-2">
                #{isBuy ? 'REQ-7823' : 'RET-4521'} · Estado:
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-300 ml-1">Aprobado</span>
              </p>
              <p className="text-sm text-green-400 font-bold mb-4">
                +{parsedAmount.toFixed(2)} 🔥 acreditados
              </p>
              <button
                type="button"
                onClick={handleContinue}
                className="py-2 px-6 rounded-xl bg-gradient-to-r from-fire-orange to-fire-yellow text-dark font-black shadow-fire hover:scale-[1.02] transition-transform"
              >
                Continuar
              </button>
            </motion.div>
          )}

          {step === 'processed' && (
            <motion.div
              key="processed"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-4"
            >
              <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <Check size={32} className="text-green-400" />
              </div>
              <h4 className="font-bold text-white mb-1">
                Retiro procesado
              </h4>
              <p className="text-xs text-white/60 mb-2">
                #RET-4521 · Estado:
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-300 ml-1">Completado</span>
              </p>
              <p className="text-sm text-green-400 font-bold mb-1">
                {parsedAmount.toFixed(2)} 🔥 retirados
              </p>
              <p className="text-[11px] text-white/50 mb-1">
                {method === 'usdt' ? `~${estimatedUsdt.toFixed(2)} USD enviados a tu wallet` : `${(parsedAmount / firesPerUsdt * 40).toFixed(2)} Bs enviados a tu banco`}
              </p>
              <p className="text-[11px] text-white/50 mb-4">
                {method === 'usdt' ? `(${txHash.slice(0, 10)}...)` : `(${bank})`}
              </p>
              <button
                type="button"
                onClick={handleContinue}
                className="py-2 px-6 rounded-xl bg-gradient-to-r from-fire-orange to-fire-yellow text-dark font-black shadow-fire hover:scale-[1.02] transition-transform"
              >
                Continuar
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SimulatedWalletFlow;
